import React, { useMemo, useState } from 'react';
import { Button, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import {
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useI18n } from '../i18n';
import { formatDate } from '../utils/formatters';
import { derivedSupplierTypes, DERIVED_SUPPLIER_TYPES } from '../utils/supplierHelpers';
import { COUNTRIES } from '../constants/countries';

const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;

export default function LeadsListPage({
  leads,
  suppliers,
  supplierProducts = [],
  services = [],
  logisticsItems = [],
  getFilteredLeads,
  getClientComments,
  clientTypeFilter,
  setClientTypeFilter,
  searchText,
  setSearchText,
  openLeadProfile,
  openSupplierProfile,
  showModal,
  setEditingSupplier,
  canDelete = true,
  scope = 'customers',
}) {
  const { t } = useI18n();
  const [supplierTypeFilter, setSupplierTypeFilter] = useState([]);
  const [supplierCountryFilter, setSupplierCountryFilter] = useState([]);

  const filteredLeads = useMemo(() => {
    const counterparties = scope === 'suppliers'
      ? suppliers.map((supplier) => ({
        ...supplier,
        key: `supplier-${supplier.id}`,
        entityKind: 'supplier',
        typeLabel: 'Supplier',
        createdLabel: supplier.created,
        commentsCount: getClientComments(supplier.id).length,
        derivedTypes: derivedSupplierTypes(supplier.id, supplierProducts, services, logisticsItems),
      }))
      : getFilteredLeads().map((lead) => ({
        ...lead,
        entityKind: 'client',
        typeLabel: lead.status === 'Lead/Prospect' ? 'Prospect' : 'Customer',
        createdLabel: lead.created,
        commentsCount: getClientComments(lead.id).length,
      }));

    return counterparties.filter((lead) => {
      if (scope !== 'suppliers') {
        if (clientTypeFilter === 'Customer' && lead.typeLabel !== 'Customer') return false;
        if (clientTypeFilter === 'Prospect' && lead.typeLabel !== 'Prospect') return false;
      } else {
        if (supplierTypeFilter.length > 0 && !supplierTypeFilter.some((type) => lead.derivedTypes.includes(type))) return false;
        if (supplierCountryFilter.length > 0 && !supplierCountryFilter.includes(lead.country)) return false;
      }
      if (searchText) {
        const query = searchText.toLowerCase();
        if (
          !lead.name.toLowerCase().includes(query) &&
          !String(lead.cvr || '').includes(query) &&
          !String(lead.email || '').toLowerCase().includes(query) &&
          !String(lead.phone || '').includes(query)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [clientTypeFilter, getClientComments, getFilteredLeads, logisticsItems, scope, searchText, services, suppliers, supplierProducts, supplierTypeFilter, supplierCountryFilter]);

  const columns = [
    {
      title: t(scope === 'suppliers' ? 'Supplier Name' : 'Client Name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => {
            if (record.entityKind === 'supplier') openSupplierProfile(record, 'suppliers');
            else openLeadProfile(record, 'leads');
          }}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    ...(scope === 'suppliers' ? [{
      title: t('Type'),
      key: 'derivedTypes',
      width: 200,
      render: (_, record) => (
        record.derivedTypes.length > 0
          ? (
            <Space size={4} wrap>
              {record.derivedTypes.map((type) => (
                <Tag key={type} style={{ fontSize: 11, margin: 0 }}>{t(type)}</Tag>
              ))}
            </Space>
          )
          : <span style={{ color: '#bfbfbf' }}>—</span>
      ),
    }, {
      title: t('Country'),
      dataIndex: 'country',
      key: 'country',
      width: 120,
      render: (country) => country ? t(country) : <span style={{ color: '#bfbfbf' }}>—</span>,
    }] : [{
      title: 'CVR',
      dataIndex: 'cvr',
      key: 'cvr',
      width: 110,
    }]),
    {
      title: t('Contact Channels'),
      key: 'channels',
      width: 230,
      render: (_, record) => (
        <div style={{ lineHeight: 1.8 }}>
          <div style={{ color: '#595959', fontSize: 13 }}>{record.email}</div>
          <div style={{ color: '#8c8c8c', fontSize: 13 }}>{record.phone}</div>
        </div>
      ),
    },
    {
      title: t('Created'),
      dataIndex: 'createdLabel',
      key: 'createdLabel',
      width: 120,
      render: (date) => date ? formatDate(date) : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Comments'),
      key: 'comments',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<CommentOutlined />}
          onClick={() => showModal('viewComments', { clientId: record.id, clientName: record.name })}
          style={{ padding: 0 }}
        >
          {record.commentsCount}
        </Button>
      ),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              if (record.entityKind === 'supplier') {
                setEditingSupplier(record);
                showModal('editSupplier');
              } else {
                showModal('editLead', record);
              }
            }}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: t('Delete Counterparty'),
                  icon: <ExclamationCircleOutlined />,
                  content: t('Are you sure you want to delete this entry?'),
                  onOk() {
                    message.success(t('Deleted'));
                  },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-toolbar" style={{ marginBottom: '16px' }}>
        <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t(scope === 'suppliers' ? 'Suppliers' : 'Customers')}</Title>
        <div className="page-toolbar-controls">
          <Input
            placeholder={t('Search by name, phone, email, or CVR')}
            style={{ width: 320, height: 32, display: 'flex', alignItems: 'center' }}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            suffix={<SearchOutlined />}
          />
          {scope !== 'suppliers' ? (
            <Select
              value={clientTypeFilter}
              onChange={setClientTypeFilter}
              style={{ width: 170 }}
            >
              <Option value="all">{t('All types')}</Option>
              <Option value="Customer">{t('Customer')}</Option>
              <Option value="Prospect">{t('Prospect')}</Option>
            </Select>
          ) : (
            <Select
              mode="multiple"
              value={supplierTypeFilter}
              onChange={setSupplierTypeFilter}
              placeholder={t('All Types')}
              style={{ minWidth: 220 }}
              maxTagCount="responsive"
            >
              {DERIVED_SUPPLIER_TYPES.map((type) => (
                <Option key={type} value={type}>{t(type)}</Option>
              ))}
            </Select>
          )}
          {scope === 'suppliers' && (
            <Select
              mode="multiple"
              value={supplierCountryFilter}
              onChange={setSupplierCountryFilter}
              placeholder={t('All Countries')}
              style={{ minWidth: 220 }}
              maxTagCount="responsive"
            >
              {COUNTRIES.map((option) => (
                <Option key={option} value={option}>{t(option)}</Option>
              ))}
            </Select>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            style={{ minWidth: 164 }}
            onClick={() => showModal(scope === 'suppliers' ? 'createSupplier' : 'createLead')}
          >
            {t(scope === 'suppliers' ? 'Add New Supplier' : 'Add New Lead/Client')}
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={filteredLeads}
        rowKey="key"
        pagination={{ pageSize: 10, position: ['bottomCenter'] }}
        bordered
        size="middle"
      />
    </div>
  );
}
